import { useEffect, useRef, useState } from "react";
import { Game } from "@/game/engine";
import { Game as ClassicGame } from "@/game/engineClassic";
import { W, H } from "@/game/config";
import { setMuted, isMuted, playMusic, stopMusic, getVolumes, setVolume } from "@/game/audio";
import { Slider } from "@/components/ui/slider";
import { HUD, HudOverlay } from "./HUD";
import { TouchControls } from "./TouchControls";
import { PixelButton } from "./PixelButton";

export const VolumeControls = () => {
  const [v, setV] = useState(getVolumes());
  const change = (kind) => ([val]) => { setVolume(kind, val / 100); setV((o) => ({ ...o, [kind]: val / 100 })); };
  return (
    <div className="volume-controls" data-testid="volume-controls">
      {[["music", "Music"], ["sfx", "Sound Effects"]].map(([k, label]) => (
        <label key={k} className="volume-row">
          <span>{label}</span>
          <Slider value={[Math.round(v[k] * 100)]} max={100} step={5} onValueChange={change(k)} data-testid={`volume-${k}-slider`} className="volume-slider" />
          <strong data-testid={`volume-${k}-value`}>{Math.round(v[k] * 100)}</strong>
        </label>
      ))}
    </div>
  );
};

export const GameScreen = ({ difficulty, opts, mode, onAchieve, onDefeat, onGameOver, onVictory, onDailyComplete, onQuit }) => {
  const canvasRef = useRef(null);
  const game = useRef(null);
  const cbs = useRef({});
  const mounted = useRef(false);
  const [hud, setHud] = useState(null);
  const [paused, setPaused] = useState(false);
  const [muted, setMute] = useState(isMuted());
  const classic = mode === "classic";

  cbs.current = { onAchieve, onDefeat, onGameOver, onVictory, onDailyComplete };

  useEffect(() => {
    const Engine = classic ? ClassicGame : Game;
    const g = new Engine(canvasRef.current, difficulty, {
      onHud: setHud,
      onAchieve: (id) => cbs.current.onAchieve(id),
      onDefeat: (id) => cbs.current.onDefeat?.(id),
      onGameOver: (s) => cbs.current.onGameOver(s),
      onVictory: (s) => cbs.current.onVictory(s),
      onDailyComplete: (s) => cbs.current.onDailyComplete?.(s),
      onPauseToggle: () => setPaused((p) => !p),
    }, opts);
    game.current = g;
    window.__game = g;
    return () => { g.destroy(); stopMusic(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [difficulty]);

  useEffect(() => {
    const g = game.current;
    if (!g) return;
    g.paused = paused;
    document.activeElement?.blur?.();
    if (!mounted.current) { mounted.current = true; return; }
    if (paused) stopMusic();
    else if (g.phase !== "done") playMusic((g.bossActive && !g.bossDead) || (classic && g.boss && !g.bossDead) ? "boss" : "level", g.run.level);
  }, [paused, classic]);

  const toggleMute = () => { setMuted(!muted); setMute(!muted); };

  return (
    <div className="game-screen" data-testid="game-screen" data-mode={mode}>
      {hud && <HUD hud={hud} onPause={() => setPaused(true)} muted={muted} onMute={toggleMute} />}
      <div className="game-stage">
        <div className="canvas-wrap">
          <canvas ref={canvasRef} width={W} height={H} className={`game-canvas ${classic ? "pixel-classic" : ""}`} data-testid="game-canvas-element" />
          {hud && <HudOverlay hud={hud} />}
          {paused && (
            <div className="pause-overlay" data-testid="pause-overlay">
              <h2>Paused</h2>
              <VolumeControls />
              <PixelButton onClick={() => setPaused(false)} data-testid="resume-button">Resume</PixelButton>
              <PixelButton variant="ghost" onClick={onQuit} data-testid="quit-button">Quit to Title</PixelButton>
            </div>
          )}
        </div>
      </div>
      <TouchControls game={game} classic={classic} />
    </div>
  );
};
